import React from "react";
import { Printer, Check, Store, X } from "lucide-react";
import { formatCurrency, formatDateTime } from "../../utils/formatters";

export function ReceiptModal({ isOpen, onClose, order }) {
  if (!isOpen || !order) return null;

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl p-6 max-w-sm w-full space-y-4 text-slate-900 dark:text-slate-100">
        {/* Actions Bar */}
        <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
          <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-600 dark:text-emerald-400">
            <Check className="w-4 h-4" />
            Sale Completed
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="p-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 rounded-lg text-slate-700 dark:text-slate-300 transition"
              title="Print Thermal 80mm Receipt"
            >
              <Printer className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Thermal 80mm Receipt Simulation */}
        <div
          id="thermal-receipt"
          className="p-4 bg-slate-50 dark:bg-slate-950 border border-dashed border-slate-300 dark:border-slate-800 rounded-xl font-mono text-[11px] space-y-2 text-slate-800 dark:text-slate-200"
        >
          <div className="text-center space-y-0.5">
            <h3 className="font-extrabold text-sm uppercase tracking-wider">
              KIRANA STORE INVENTORY (HYDERABAD)
            </h3>
            <p className="text-[10px] text-slate-500">Begum Bazar & KPHB Superstore Hub</p>
            <p className="text-[9px] text-slate-400">Tilak Road, Near Abids GPO, Hyderabad 500001</p>
            <p className="text-[9px] text-slate-400">Telangana GSTIN: 36AABCK9928M1Z8 • Ph: 98490 88990</p>
          </div>

          <div className="border-t border-b border-slate-300 dark:border-slate-800 py-1.5 space-y-0.5 text-[10px]">
            <div className="flex justify-between">
              <span>Bill No: {order.orderNumber}</span>
              <span>{order.channel}</span>
            </div>
            <div className="flex justify-between text-slate-500">
              <span>Date: {formatDateTime(order.date || new Date())}</span>
            </div>
            <div className="flex justify-between text-slate-500">
              <span>Customer: {order.customerName || "Retail Walk-in"}</span>
            </div>
          </div>

          {/* Line items */}
          <div className="space-y-1 py-1">
            <div className="flex justify-between font-bold border-b border-slate-200 dark:border-slate-800 pb-0.5">
              <span>Item</span>
              <span>Qty x Price</span>
              <span>Total</span>
            </div>
            {order.items?.map((item, idx) => (
              <div key={idx} className="flex justify-between">
                <span className="truncate max-w-[130px]">{item.productName}</span>
                <span>
                  {item.qty} x ₹{item.sellingPrice}
                </span>
                <span className="font-bold">₹{item.total}</span>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="border-t border-slate-300 dark:border-slate-800 pt-1.5 space-y-0.5 text-xs">
            <div className="flex justify-between text-slate-500 text-[11px]">
              <span>Subtotal:</span>
              <span>{formatCurrency(order.subtotal)}</span>
            </div>
            <div className="flex justify-between text-slate-500 text-[11px]">
              <span>GST Tax:</span>
              <span>{formatCurrency(order.taxGst || 0)}</span>
            </div>
            {order.discountAmount > 0 && (
              <div className="flex justify-between text-rose-500 text-[11px]">
                <span>Discount:</span>
                <span>-{formatCurrency(order.discountAmount)}</span>
              </div>
            )}
            <div className="flex justify-between font-extrabold text-sm text-slate-900 dark:text-white pt-1 border-t border-slate-300 dark:border-slate-800">
              <span>GRAND TOTAL:</span>
              <span className="text-emerald-600 dark:text-emerald-400">
                {formatCurrency(order.grandTotal)}
              </span>
            </div>
            <div className="flex justify-between text-[10px] text-slate-500 pt-1">
              <span>Payment Mode:</span>
              <span className="font-bold">{order.paymentMethod}</span>
            </div>
          </div>

          <div className="text-center pt-2 border-t border-dashed border-slate-300 dark:border-slate-800 text-[10px] text-slate-400">
            <p>Thank you for shopping with us!</p>
            <p>*** Please visit again ***</p>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <button
            onClick={onClose}
            className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl text-xs shadow-lg shadow-emerald-600/30 transition"
          >
            Done / Next Customer (Enter)
          </button>
        </div>
      </div>
    </div>
  );
}
